import React, {useMemo} from 'react';
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import type {HotspotExplorerProps, HotspotExplorerSortMode} from "../types";
import {
    buildHotspotDetailPath,
    buildStatePath,
    getCountryLabel,
    getHotspotExplorerHeroImage,
    getHotspotExplorerStats,
    getVisibleHotspots,
    sortHotspots
} from "../helpers";
import {HotspotExplorerShell} from "../hotspot-explorer-shell";

export function CountryHotspotsView({hotspotExplorerIndex}: HotspotExplorerProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const {countryCode = ''} = useParams();
    const sortMode = (searchParams.get('sort') as HotspotExplorerSortMode) || 'visits';
    const hotspots = useMemo(() => hotspotExplorerIndex.hotspotsByCountryCode.get(countryCode) ?? [], [countryCode, hotspotExplorerIndex]);
    const regionBounds = hotspots[0]?.regionData?.country?.bounds ?? undefined;
    const visibleHotspots = useMemo(
        () => sortHotspots(getVisibleHotspots(hotspots, {countryCode}), sortMode),
        [countryCode, hotspots, sortMode]
    );
    const stateGroups = hotspotExplorerIndex.stateGroupsByCountryCode.get(countryCode) ?? [];

    const setSortMode = (nextSortMode: HotspotExplorerSortMode) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('sort', nextSortMode);
        setSearchParams(nextParams, {replace: true});
    }

    const openState = (stateProvince: string) => {
        navigate(`${buildStatePath(countryCode, stateProvince)}${location.search}`);
    }

    const openHotspot = (locationId: string) => {
        navigate(buildHotspotDetailPath(locationId), {
            state: {returnTo: `${location.pathname}${location.search}`}
        });
    }

    return (
        <HotspotExplorerShell
            backgroundImage={getHotspotExplorerHeroImage({routeLevel: 'country', regionBounds})}
            eyebrow="Hotspot Explorer"
            title={getCountryLabel(countryCode)}
            intro={`Hotspots you've visited in ${getCountryLabel(countryCode)}.`}
            backLabel="Back to Countries"
            onBack={() => navigate('/hotspot-explorer')}
            stats={getHotspotExplorerStats(visibleHotspots, 'Counties')}
            panelHeading="By State/Province"
            groups={stateGroups}
            activeGroupCode={undefined}
            onSelectGroup={openState}
            hotspots={visibleHotspots}
            sortMode={sortMode}
            onChangeSort={setSortMode}
            onOpenHotspot={openHotspot}
        />
    );
}
